import React, { useCallback, useEffect, useRef, useState } from 'react';
import HoverPO from '../HoverPO';
import { useCommentDock } from '../../context/CommentDockContext';
import { usePresenceContext } from '../../context/PresenceContext';

const WINDOW_WIDTH = 380;
const WINDOW_HEIGHT = 480;

const clampPosition = (left, top, width, height) => {
    const maxLeft = Math.max(0, window.innerWidth - width);
    const maxTop = Math.max(0, window.innerHeight - height);
    return {
        left: Math.min(Math.max(0, left), maxLeft),
        top: Math.min(Math.max(0, top), maxTop),
    };
};

const CommentThreadWindow = ({ thread, stackIndex = 0 }) => {
    const { minimizeThread, closeThread, updateThreadComments, updateThreadPosition, getHandlers, patchThread } = useCommentDock() || {};
    const presence = usePresenceContext();
    const handlers = getHandlers ? getHandlers(thread.key) : {};

    // When the row is on screen its handler updates the grid and publishes the comment.
    // A chat opened from a notification has no row handler, so publish the same event here.
    const handleAdded = (comment, status) => {
        // Replying means the "New" comments have been read; stop marking them.
        if (thread.highlightCount) patchThread?.(thread.key, { highlightCount: 0 });
        if (handlers.onAddComment) {
            handlers.onAddComment(comment, status);
            return;
        }
        const payload = {
            kind: 'comment',
            value: comment,
            customerName: thread.subtitle || '',
            orderNumber: thread.orderNumber || '',
        };
        if (thread.quoteId != null && thread.quoteId !== '') {
            payload.quoteId = String(thread.quoteId);
            if (!payload.orderNumber) payload.orderNumber = String(thread.quoteId);
        } else if (thread.poLineId != null && thread.poLineId !== '') {
            payload.poLineId = String(thread.poLineId);
            payload.lineId = String(thread.poLineId);
        } else if (thread.orderLineId != null) {
            payload.lineId = String(thread.orderLineId);
        } else {
            return;
        }
        presence?.broadcastSave?.(payload);
    };
    const windowRef = useRef(null);
    const dragRef = useRef(null);
    const posRef = useRef(null);
    const [pos, setPos] = useState(() => {
        if (!thread.pos || !Number.isFinite(thread.pos.left) || !Number.isFinite(thread.pos.top)) return null;
        return clampPosition(thread.pos.left, thread.pos.top, WINDOW_WIDTH, WINDOW_HEIGHT);
    });
    const [dragging, setDragging] = useState(false);
    posRef.current = pos;

    const offset = stackIndex * 28;
    const style = pos
        ? {
            left: `${pos.left}px`,
            top: `${pos.top}px`,
            right: 'auto',
            bottom: 'auto',
            zIndex: dragging ? 1000010 : 1000006 + stackIndex,
        }
        : {
            right: `${24 + offset}px`,
            bottom: `${96 + offset}px`,
            zIndex: 1000006 + stackIndex,
        };

    const onHeaderPointerDown = useCallback((e) => {
        if (e.button != null && e.button !== 0) return;
        if (e.target.closest && e.target.closest('.comment-dock-header-btn')) return;
        const node = windowRef.current;
        if (!node) return;
        e.preventDefault();
        e.stopPropagation();
        const rect = node.getBoundingClientRect();
        dragRef.current = {
            offsetX: e.clientX - rect.left,
            offsetY: e.clientY - rect.top,
            width: rect.width || WINDOW_WIDTH,
            height: rect.height || WINDOW_HEIGHT,
        };
        setPos({ left: rect.left, top: rect.top });
        setDragging(true);
        if (e.currentTarget.setPointerCapture && e.pointerId != null) {
            try {
                e.currentTarget.setPointerCapture(e.pointerId);
            } catch (err) {
                /* capture is optional */
            }
        }
    }, []);

    useEffect(() => {
        if (!dragging) return undefined;

        const previousCursor = document.body.style.cursor;
        const previousSelect = document.body.style.userSelect;
        document.body.style.cursor = 'grabbing';
        document.body.style.userSelect = 'none';

        const onMove = (e) => {
            const drag = dragRef.current;
            if (!drag) return;
            const clientX = e.clientX ?? e.touches?.[0]?.clientX;
            const clientY = e.clientY ?? e.touches?.[0]?.clientY;
            if (clientX == null || clientY == null) return;
            setPos(clampPosition(
                clientX - drag.offsetX,
                clientY - drag.offsetY,
                drag.width,
                drag.height
            ));
        };

        const onUp = () => {
            setDragging(false);
            dragRef.current = null;
            if (posRef.current) updateThreadPosition?.(thread.key, posRef.current);
        };

        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        window.addEventListener('pointercancel', onUp);
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        window.addEventListener('touchmove', onMove, { passive: false });
        window.addEventListener('touchend', onUp);

        return () => {
            document.body.style.cursor = previousCursor;
            document.body.style.userSelect = previousSelect;
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            window.removeEventListener('pointercancel', onUp);
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            window.removeEventListener('touchmove', onMove);
            window.removeEventListener('touchend', onUp);
        };
    }, [dragging, thread.key, updateThreadPosition]);

    return (
        <div
            ref={windowRef}
            className={`comment-dock-window${thread.minimized ? ' is-hidden' : ''}${dragging ? ' is-dragging' : ''}`}
            style={style}
            data-comment-thread={thread.key}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
        >
            <div
                className="comment-dock-header"
                title="Drag to move"
                onPointerDown={onHeaderPointerDown}
            >
                <span className="comment-dock-drag-grip" aria-hidden="true" />
                <div className="comment-dock-header-text">
                    <div className="comment-dock-title">{thread.title}</div>
                    {thread.subtitle ? (
                        <div className="comment-dock-subtitle">{thread.subtitle}</div>
                    ) : null}
                    {thread.lineInfo ? (
                        <div className="comment-dock-lineinfo">{thread.lineInfo}</div>
                    ) : null}
                </div>
                <div className="comment-dock-header-actions">
                    <button
                        type="button"
                        className="comment-dock-header-btn"
                        title="Minimize"
                        aria-label="Minimize"
                        onPointerDown={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={() => minimizeThread?.(thread.key)}
                    >
                        −
                    </button>
                    <button
                        type="button"
                        className="comment-dock-header-btn"
                        title="Close"
                        aria-label="Close"
                        onPointerDown={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={() => closeThread?.(thread.key)}
                    >
                        ×
                    </button>
                </div>
            </div>
            {(thread.loading || thread.loadError) && (
                <div className={`comment-dock-status${thread.loadError ? ' is-error' : ''}`} role="status">
                    {thread.loadError || 'Loading comments…'}
                </div>
            )}
            {thread.orderLineId != null || thread.quoteId != null || thread.poLineId != null ? (
                <HoverPO
                    variant="docked"
                    comments={thread.comments}
                    orderLineId={thread.orderLineId}
                    quoteId={thread.quoteId}
                    poLineId={thread.poLineId}
                    orderNumber={thread.orderNumber || thread.title}
                    customerName={thread.subtitle}
                    customerId={thread.customerId}
                    sourcePage={thread.sourcePage}
                    highlightCount={thread.highlightCount || 0}
                    onAddComment={handleAdded}
                    onDeleteComment={(commentId) => handlers.onDeleteComment?.(commentId)}
                    onResolveThread={(commentId) => handlers.onResolveThread?.(commentId)}
                    onCommentsChange={(comments) => updateThreadComments?.(thread.key, comments)}
                />
            ) : (
                // Opened from a notification and still finding which order line it belongs to.
                <div className="comment-dock-thread-body" />
            )}
        </div>
    );
};

export default CommentThreadWindow;
